/** Subscription tier keys — mirrors Postgres `subscription_tier` enum order. */
export type SubscriptionTier = 'free' | 't25' | 't100' | 't200' | 't500';

export const TIER_DISPLAY_NAME: Record<SubscriptionTier, string> = {
  free: 'Free',
  t25: 'Starter',
  t100: 'Team',
  t200: 'Business',
  t500: 'Enterprise'
};

/** Seat cap the server uses for "uncapped" Enterprise (`max_seats = 2147483647`). */
export const UNCAPPED_MAX_SEATS = 1_000_000;

export interface PublicTierPlan {
  tier: SubscriptionTier;
  /** USD per month — verified against live Stripe Products (May 2026). */
  monthlyPriceUsd: number;
  maxSeats: number;
  /** Short line for the public landing page. */
  summary: string;
  /** Visually emphasize on marketing (e.g. best fit for small teams). */
  highlighted?: boolean;
}

/**
 * Public tier catalog for anonymous surfaces (landing page). Seat caps mirror
 * `subscription_tier_config`; monthly prices mirror Stripe recurring USD amounts
 * (Starter $25 · Team $100 · Business $200 · Enterprise $500).
 */
export const PUBLIC_TIER_CATALOG: ReadonlyArray<PublicTierPlan> = [
  {
    tier: 'free',
    monthlyPriceUsd: 0,
    maxSeats: 1,
    summary:
      'Solo devs — local and cloud storage, documents, agile workflow, todos, and agent rules.'
  },
  {
    tier: 't25',
    monthlyPriceUsd: 25,
    maxSeats: 2,
    summary: 'Pair programming — shared org workspace for you and one teammate.'
  },
  {
    tier: 't100',
    monthlyPriceUsd: 100,
    maxSeats: 5,
    highlighted: true,
    summary: 'Small teams — specs, tasks, and role rules in one place for everyone.'
  },
  {
    tier: 't200',
    monthlyPriceUsd: 200,
    maxSeats: 11,
    summary: 'Growing squads — more seats with the same role-aware agent stack.'
  },
  {
    tier: 't500',
    monthlyPriceUsd: 500,
    maxSeats: UNCAPPED_MAX_SEATS,
    summary: 'Large orgs — unlimited seats and full workspace for every role.'
  }
] as const;

export function formatTierSeatLabel(maxSeats: number): string {
  if (maxSeats >= UNCAPPED_MAX_SEATS) return 'Unlimited seats';
  return `${maxSeats} ${maxSeats === 1 ? 'seat' : 'seats'}`;
}
